import { gql } from "@apollo/client";
import { PRODUCT_ID_FIELDS } from "../fragments/product";

const TRANSACTION_META_NOTES = `
  meta {
    notes
    isDeleted
  }
`;

const sharedTransactionFields = `
  _id
  friendlyId
  createdAt
  transactionAmount
  transactionDirection
  transactionStatus
  transactionType
  completionDate
  owner
  asset
  senderCollection
  recipientCollection
  denormalized {
    donorName
    productName
    assetName
    senderName
    recipientName
  }
  ${TRANSACTION_META_NOTES}
`;

const assetTransferFields = `
  ${sharedTransactionFields}
  transferGroupId
  counterpartProduct
  counterpartProductName
  counterpartTransaction
  senderDonorName
  recipientDonorName
  transferAssetType
  allocationAmount
  initializationDate
  ticker
  quantity
  fairMarketValue
  cryptoTransactionId
  equityAmount
  equityType
  stocks {
    _id
    ticker
    quantity
    high
    low
    fairMarketValue
  }
`;

const DONOR_GRANT_TRANSACTIONS = gql`
  query DonorGrantTransaction($productId: ID) {
    donorGrantTransactions(productId: $productId) {
      _id
      friendlyId
      createdAt
      transactionAmount
      transactionDirection
      transactionStatus
      transactionType
      completionDate
      denormalized {
        donorName
        productName
        assetName
        senderName
        recipientName
      }
      recipient {
        ... on OrganizationEntity {
          _id
          entityName
          approvedForGranting
          ein
          charityType
          address {
            address1
            address2
            city
            country
            state
            zip
          }
          increase {
            achTransfers
            wireTransfers
          }
        }
      }
      complianceReview {
        lane
        reasonCode
        feeAmount
      }
      acknowledgement {
        acknowledgementType
        address {
          address1
          address2
          city
          country
          state
          zip
        }
        customAcknowledgement
        name
        use
        ldsUse {
          mrn
          ward
          stake
          tithing
          fastOffering
          generalMissionaryFund
          wardMissionaryFund
          humanitarianAid
          other
          notes
        }
      }
      recipientCollection
      cancellationDate
      cancellationReason
      submittedDate
      paymentReceived
      checkNumber
      paymentSent
      paymentMethod
      paymentProcessor
      expedite
    }
  }
`;

const PRODUCT_TRANSACTIONS = gql`
  query ProductTransactions($productId: ID, $transactionType: TransactionType, $includeDeleted: Boolean) {
    productTransactions(productId: $productId, transactionType: $transactionType, includeDeleted: $includeDeleted) {
      ... on GrantTransaction {
        ${sharedTransactionFields}
        acknowledgement {
          acknowledgementType
          address {
            address1
            address2
            city
            country
            state
            zip
          }
          customAcknowledgement
          name
          use
        }
        recipientCollection
        cancellationDate
        cancellationReason
        submittedDate
        paymentReceived
        checkNumber
        paymentSent
        expedite
        # asset
        # requestedByUserId
        # senderCollection
      }
      ... on CashContributionTransaction {
        ${sharedTransactionFields}
        contributionAmount
        completedTransfer
        donationDate
        paymentMethod
        paymentProcessor
        recipientCollection
        updatedAt
        settledInClearing
        requestedTransferFromClearing
        paystand {
          paymentId
          status
        }
        plaidSweepStatus
        plaidSweepDate
        plaidSweepId
        # asset
        # requestedByUserId
        # senderCollection
      }
      ... on DafTransferTransaction {
        ${sharedTransactionFields}
        contributionAmount
        completedTransfer
        donationDate
        paymentMethod
        paymentProcessor
        recipientCollection
        updatedAt
        settledInClearing
        requestedTransferFromClearing
      }
      ... on AssetTransferTransaction {
        ${assetTransferFields}
      }
      ... on StockContributionTransaction {
        donationDate
        contributionAmount
        stocks {
          _id
          fairMarketValue
          high
          low
          quantity
          ticker
        }
        ${sharedTransactionFields}
      }
      ... on CryptoContributionTransaction {
        ${sharedTransactionFields}
        contributionAmount
        ticker
        quantity
        cryptoTransactionId
        donationDate
        documents {
          _id
          fileCategory
          executionDate
          createdAt
        }
      }
      ... on EquityInvestmentTransaction {
        liquidationRequestSent
        equityType
        equityAmount
        paymentReceived
        documents {
          _id
          fileCategory
          executionDate
          createdAt
        }
        ${sharedTransactionFields}
      }
      ... on FundInvestmentTransaction {
        ${sharedTransactionFields}
      }
      ... on EquityContributionTransaction {
        donationDate
        contributionAmount
        equityType
        equityAmount
        acknowledgement {
          use
        }
        documents {
          _id
          fileCategory
          executionDate
          createdAt
        }
        ${sharedTransactionFields}
      }
      ... on EquityExpenseTransaction {
        liquidationRequestSent
        paymentReceived
        ${sharedTransactionFields}
        acknowledgement {
          use
        }
      }
      ... on DistributionTransaction {
        settledInClearing
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on ReturnOfCapitalTransaction {
        settledInClearing
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on EquitySaleTransaction {
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on EquitySaleProceedsTransaction {
        dateReceived
        settledInClearing
        paymentMethod
        paymentProcessor
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on UbitEstimateTransaction {
        dateReceived
        paymentMethod
        paymentProcessor
        settledInTrust
        taxYear
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on UbitReconcileTransaction {
        dateReceived
        paymentMethod
        paymentProcessor
        settledInTrust
        taxYear
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on FundDistributionTransaction {
        ${sharedTransactionFields}
      }
      ... on LendingContributionTransaction {
        donationDate
        contributionAmount
        acknowledgement {
          use
        }
        documents {
          _id
          fileCategory
          executionDate
          createdAt
        }
        ${sharedTransactionFields}
      }
      ... on LendingInvestmentTransaction {
        liquidationRequestSent
        paymentReceived
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on LendingReturnTransaction {
        principal
        interest
        settledInClearing
        acknowledgement {
          use
        }
        ${sharedTransactionFields}
      }
      ... on RecoverableGrantTransaction {
        ${sharedTransactionFields}
      }
    }
  }
`;

const FIND_PRODUCT_TRANSACTIONS_SERVER_SIDE = gql`
  query FindProductTransactionsServerSide($productId: ID, $input: ServerQueryInput, $includeDeleted: Boolean) {
    findProductTransactionsServerSide(productId: $productId, input: $input, includeDeleted: $includeDeleted) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      transactions {
        ... on DistributionTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          settledInClearing
          acknowledgement {
            use
          }
        }
        ... on ReturnOfCapitalTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          settledInClearing
          acknowledgement {
            use
          }
        }
        ... on EquityInvestmentTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          liquidationRequestSent
          paymentReceived
          equityType
          equityAmount
          documents {
            _id
            fileCategory
            executionDate
            createdAt
          }
        }
        ... on EquityExpenseTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          paymentReceived
          liquidationRequestSent
          acknowledgement {
            use
          }
        }
        ... on EquityContributionTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          contributionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          donationDate
          equityType
          equityAmount
          documents {
            _id
            fileCategory
            executionDate
            createdAt
          }
        }
        ... on CryptoContributionTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          contributionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          donationDate
          ticker
          quantity
          cryptoTransactionId
          documents {
            _id
            fileCategory
            executionDate
            createdAt
          }
        }
        ... on GrantTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          acknowledgement {
            acknowledgementType
            address {
              address1
              address2
              city
              country
              state
              zip
            }
            customAcknowledgement
            name
            use
            ldsUse {
              mrn
              ward
              stake
              tithing
              fastOffering
              generalMissionaryFund
              wardMissionaryFund
              humanitarianAid
              other
              notes
            }
          }
          recipient {
            ... on OrganizationEntity {
              _id
              primaryEmail
              onboarding {
                contactPerson {
                  email
                }
              }
              increase {
                externalAccountId
                achTransfers
                wireTransfers
              }
            }
            ... on StubEntity {
              _id
            }
          }
          recipientCollection
          cancellationDate
          cancellationReason
          submittedDate
          paymentReceived
          checkNumber
          paymentSent
          expedite
          onHold
          paymentMethod
          paymentProcessor
          payPalEligible
          payPalPaymentStatus
          increase {
            fundsReceivedAt
            currentTransferId
            currentAttempt {
              rail
              normalizedStatus
              rawStatus
              transferId
              letterEmailedAt
              submittedAt
              completedAt
              failedAt
              failureCode
              failureReason
              expectedSettlementAt
              imad
            }
            attempts {
              attemptNumber
              rail
              normalizedStatus
              transferId
              failedAt
              failureReason
              revertedAt
            }
          }
        }
        ... on CashContributionTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          contributionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          donationDate
          paymentMethod
          paymentProcessor
          settledInClearing
          plaidSweepStatus
          plaidSweepId
          paystand {
            paymentId
            status
          }
        }
        ... on DafTransferTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          contributionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          donationDate
          paymentMethod
          paymentProcessor
          settledInClearing
        }
        ... on AssetTransferTransaction {
          ${assetTransferFields}
          ${PRODUCT_ID_FIELDS}
        }
        ... on StockContributionTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          contributionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          donationDate
          stocks {
            _id
            fairMarketValue
            high
            low
            quantity
            ticker
          }
        }
        ... on LendingContributionTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          contributionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          donationDate
          documents {
            _id
            fileCategory
            executionDate
            createdAt
          }
        }
        ... on LendingInvestmentTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          paymentReceived
          liquidationRequestSent
          documents {
            _id
            fileCategory
            executionDate
            createdAt
          }
        }
        ... on UbitEstimateTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          dateReceived
          paymentMethod
          paymentProcessor
          settledInTrust
          acknowledgement {
            use
          }
        }
        ... on UbitReconcileTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          dateReceived
          paymentMethod
          paymentProcessor
          settledInTrust
          acknowledgement {
            use
          }
        }
        ... on EquitySaleTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          acknowledgement {
            use
          }
        }
        ... on EquitySaleProceedsTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          dateReceived
          settledInClearing
          paymentMethod
          paymentProcessor
          acknowledgement {
            use
          }
        }
        ... on LendingReturnTransaction {
          _id
          friendlyId
          createdAt
          transactionAmount
          transactionDirection
          transactionStatus
          owner
          asset
          senderCollection
          recipientCollection
          transactionType
          completionDate
          denormalized {
            donorName
            productName
            assetName
            senderName
            recipientName
          }
          ${PRODUCT_ID_FIELDS}
          ${TRANSACTION_META_NOTES}
          settledInClearing
          principal
          interest
        }
      }
    }
  }
`;

const RECENT_TRANSACTIONS = gql`
query RecentTransactions($productId: ID) {
  recentTransactions(productId: $productId) {
    ... on CashContributionTransaction {
      completedTransfer
      donationDate
      ${sharedTransactionFields}
    }
    ... on DafTransferTransaction {
      completedTransfer
      donationDate
      ${sharedTransactionFields}
    }
    ... on AssetTransferTransaction {
      ${assetTransferFields}
    }
    ... on CryptoContributionTransaction {
      donationDate
      ${sharedTransactionFields}
    }
    ... on EquityContributionTransaction {
      donationDate
      ${sharedTransactionFields}
    }
    ... on EquityExpenseTransaction {
      ${sharedTransactionFields}
    }
    ... on EquityInvestmentTransaction {
      ${sharedTransactionFields}
    }
    ... on DistributionTransaction {
      ${sharedTransactionFields}
    }
    ... on ReturnOfCapitalTransaction {
      ${sharedTransactionFields}
    }
    ... on EquitySaleProceedsTransaction {
      dateReceived
      ${sharedTransactionFields}
    }
    ... on UbitEstimateTransaction {
      dateReceived
      ${sharedTransactionFields}
    }
    ... on UbitReconcileTransaction {
      dateReceived
      ${sharedTransactionFields}
    }
    ... on EquitySaleTransaction {
      ${sharedTransactionFields}
    }
    ... on FundDistributionTransaction {
      ${sharedTransactionFields}
    }
    ... on FundInvestmentTransaction {
      ${sharedTransactionFields}
    }
    ... on GrantTransaction {
      submittedDate
      expedite
      ${sharedTransactionFields}
    }
    ... on LendingContributionTransaction {
      donationDate
      ${sharedTransactionFields}
    }
    ... on LendingInvestmentTransaction {
      ${sharedTransactionFields}
    }
    ... on LendingReturnTransaction {
      ${sharedTransactionFields}
    }
    ... on StockContributionTransaction {
      donationDate
      ${sharedTransactionFields}
    }
    ... on RecoverableGrantTransaction {
      ${sharedTransactionFields}
    }
  }
}
`;

const DASHBOARD_GRANT_FIELDS = `
      _id
      friendlyId
      createdAt
      submittedDate
      transactionStatus
      transactionAmount
      checkNumber
      paymentReceived
      paymentSent
      payPalPaymentStatus
      paymentMethod
      paymentProcessor
      donorConfirmationEmailedAt
      denormalized {
        donorName
        productName
        recipientName
      }
      increase {
        fundsReceivedAt
        currentTransferId
        currentAttempt {
          rail
          normalizedStatus
          letterEmailedAt
          failedAt
          failureReason
        }
        letterAlreadyEmailed
      }
      liquidationRequestSent
      completionDate
      expedite
      onHold
      ${TRANSACTION_META_NOTES}
      acknowledgement {
        acknowledgementType
        address {
          address1
          address2
          city
          country
          state
          zip
        }
        name
        use
      }
      recipient {
        ... on OrganizationEntity {
          _id
          entityName
          ein
          primaryEmail
          increase {
            externalAccountId
            achTransfers
            wireTransfers
          }
          onboarding {
            contactPerson {
              email
            }
          }
        }
      }
      sender {
        ... on AccountAsset {
          _id
          accountLogin
        }
      }
      product {
        ... on DafProduct {
          _id
          name
          productType
          users {
            _id
            user {
              ... on DonorUser {
                _id
                firstName
                lastName
                email
                denormalized {
                  firmName
                }
              }
            }
          }
        }
        ... on ClatProduct {
          _id
          name
          productType
          users {
            _id
            user {
              ... on DonorUser {
                _id
                firstName
                lastName
                email
                denormalized {
                  firmName
                }
              }
            }
          }
        }
        ... on CommunityFundProduct {
          _id
          name
          productType
          users {
            _id
            user {
              ... on DonorUser {
                _id
                firstName
                lastName
                email
                denormalized {
                  firmName
                }
              }
            }
          }
        }
      }
`;

const FIND_DASHBOARD_PENDING_GRANTS = gql`
  query FindDashboardPendingGrants($input: ServerQueryInput) {
    findDashboardPendingGrants(input: $input) {
      transactions {
        ... on GrantTransaction {
          ${DASHBOARD_GRANT_FIELDS}
        }
      }
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
    }
  }
`;

const FIND_DASHBOARD_COMPLIANCE_REVIEW_GRANTS = gql`
  query FindDashboardComplianceReviewGrants($input: ServerQueryInput) {
    findDashboardComplianceReviewGrants(input: $input) {
      transactions {
        ... on GrantTransaction {
          ${DASHBOARD_GRANT_FIELDS}
          useReviewedAt
          agreementReceivedAt
          complianceSnapshot {
            source
            charityTypes
            capturedAt
          }
          complianceReview {
            feeAmount
            waitingOn
            readyToRelease
            feeAlreadyRecorded
            invoiceYear
            invoiceQuarter
          }
          recipient {
            ... on OrganizationEntity {
              approvedForGranting
              compliance {
                eoCategory
                supportingOrgType
                dueDiligence {
                  completedAt
                  expiresAt
                }
              }
            }
          }
        }
      }
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
    }
  }
`;

const FIND_DASHBOARD_IN_TRANSIT_GRANTS = gql`
  query FindDashboardInTransitGrants($input: ServerQueryInput) {
    findDashboardInTransitGrants(input: $input) {
      transactions {
        ... on GrantTransaction {
          ${DASHBOARD_GRANT_FIELDS}
        }
      }
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
    }
  }
`;

const FIND_DASHBOARD_FLOATING_GRANTS = gql`
  query FindDashboardFloatingGrants($input: ServerQueryInput) {
    findDashboardFloatingGrants(input: $input) {
      transactions {
        ... on GrantTransaction {
          ${DASHBOARD_GRANT_FIELDS}
        }
      }
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
    }
  }
`;

const FIND_ALL_INCOMING_TRANSACTIONS = gql`
  query FindAllIncomingTransactions {
    findAllIncomingTransactions {
      ... on CashContributionTransaction {
        _id
        friendlyId
        owner
        product {
          ... on DafProduct {
            _id
            productType
          }
          ... on ClatProduct {
            _id
            productType
          }
          ... on CommunityFundProduct {
            _id
            productType
          }
        }
        denormalized {
          donorName
          productName
          senderName
        }
        ${TRANSACTION_META_NOTES}
        completionDate
        donationDate
        paystand {
          paymentId
          status
          payerTotalFees
        }
        settledInClearing
        requestedTransferFromClearing
        transactionAmount
        contributionAmount
        transactionType
        recipient {
          ... on AccountAsset {
            _id
            institution
          }
        }
      }
      ... on DafTransferTransaction {
        _id
        friendlyId
        owner
        product {
          ... on DafProduct {
            _id
            productType
          }
          ... on ClatProduct {
            _id
            productType
          }
          ... on CommunityFundProduct {
            _id
            productType
          }
        }
        denormalized {
          donorName
          productName
          senderName
        }
        ${TRANSACTION_META_NOTES}
        completionDate
        donationDate
        settledInClearing
        requestedTransferFromClearing
        transactionAmount
        contributionAmount
        transactionType
        recipient {
          ... on AccountAsset {
            _id
            institution
          }
        }
      }
      ... on StockContributionTransaction {
        _id
        friendlyId
        owner
        product {
          ... on DafProduct {
            _id
            productType
          }
          ... on ClatProduct {
            _id
            productType
          }
          ... on CommunityFundProduct {
            _id
            productType
          }
        }
        denormalized {
          donorName
          productName
          senderName
        }
        ${TRANSACTION_META_NOTES}
        completionDate
        donationDate
        transactionAmount
        contributionAmount
        transactionType
        recipient {
          ... on AccountAsset {
            _id
            institution
          }
        }
      }
      ... on EquitySaleProceedsTransaction {
        _id
        friendlyId
        owner
        product {
          ... on DafProduct {
            _id
            productType
          }
          ... on ClatProduct {
            _id
            productType
          }
          ... on CommunityFundProduct {
            _id
            productType
          }
        }
        denormalized {
          donorName
          productName
          senderName
        }
        ${TRANSACTION_META_NOTES}
        completionDate
        transactionAmount
        transactionType
        dateReceived
        settledInClearing
        requestedTransferFromClearing
        recipient {
          ... on AccountAsset {
            _id
            institution
          }
        }
      }
      ... on LendingReturnTransaction {
        _id
        friendlyId
        owner
        product {
          ... on DafProduct {
            _id
            productType
          }
          ... on ClatProduct {
            _id
            productType
          }
          ... on CommunityFundProduct {
            _id
            productType
          }
        }
        denormalized {
          donorName
          productName
          senderName
        }
        ${TRANSACTION_META_NOTES}
        completionDate
        transactionAmount
        transactionType
        settledInClearing
        requestedTransferFromClearing
        recipient {
          ... on AccountAsset {
            _id
            institution
          }
        }
      }
      ... on DistributionTransaction {
        _id
        friendlyId
        owner
        product {
          ... on DafProduct {
            _id
            productType
          }
          ... on ClatProduct {
            _id
            productType
          }
          ... on CommunityFundProduct {
            _id
            productType
          }
        }
        denormalized {
          donorName
          productName
          senderName
        }
        ${TRANSACTION_META_NOTES}
        completionDate
        transactionAmount
        transactionType
        settledInClearing
        requestedTransferFromClearing
        recipient {
          ... on AccountAsset {
            _id
            institution
          }
        }
      }
      ... on ReturnOfCapitalTransaction {
        _id
        friendlyId
        owner
        product {
          ... on DafProduct {
            _id
            productType
          }
          ... on ClatProduct {
            _id
            productType
          }
          ... on CommunityFundProduct {
            _id
            productType
          }
        }
        denormalized {
          donorName
          productName
          senderName
        }
        ${TRANSACTION_META_NOTES}
        completionDate
        transactionAmount
        transactionType
        settledInClearing
        requestedTransferFromClearing
        recipient {
          ... on AccountAsset {
            _id
            institution
          }
        }
      }
    }
  }
`;

const FIND_ALL_OUTGOING_TRANSACTIONS = gql`
  query FindAllOutgoingTransactions {
    findAllOutgoingTransactions {
      ... on LendingInvestmentTransaction {
        _id
        friendlyId
        ${TRANSACTION_META_NOTES}
        paymentReceived
        completionDate
        createdAt
        transactionAmount
        transactionStatus
        transactionType
        recipientCollection
        liquidationRequestSent
        recipient {
          ... on OrganizationEntity {
            _id
            entityName
          }
        }
        product {
          ... on DafProduct {
            _id
            name
            productType
            users {
              _id
              associationType
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
          ... on ClatProduct {
            _id
            name
            productType
            users {
              _id
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
          ... on CommunityFundProduct {
            _id
            name
            productType
            users {
              _id
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
        }
      }
      ... on EquityInvestmentTransaction {
        _id
        friendlyId
        ${TRANSACTION_META_NOTES}
        paymentReceived
        completionDate
        createdAt
        transactionAmount
        transactionStatus
        transactionType
        recipientCollection
        liquidationRequestSent
        recipient {
          ... on OrganizationEntity {
            _id
            entityName
          }
          ... on EquitySourceAsset {
            _id
            assetName
          }
        }
        product {
          ... on DafProduct {
            _id
            name
            productType
            users {
              _id
              associationType
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
          ... on ClatProduct {
            _id
            name
            productType
            users {
              _id
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
          ... on CommunityFundProduct {
            _id
            name
            productType
            users {
              _id
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
        }
      }
      ... on EquityExpenseTransaction {
        _id
        friendlyId
        ${TRANSACTION_META_NOTES}
        paymentReceived
        completionDate
        createdAt
        transactionAmount
        transactionStatus
        transactionType
        recipientCollection
        liquidationRequestSent
        recipient {
          ... on OrganizationEntity {
            _id
            entityName
          }
        }
        product {
          ... on DafProduct {
            _id
            name
            productType
            users {
              _id
              associationType
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
          ... on ClatProduct {
            _id
            name
            productType
            users {
              _id
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
          ... on CommunityFundProduct {
            _id
            name
            productType
            users {
              _id
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                }
              }
            }
          }
        }
      }
    }
  }
`;

const GET_TRANSACTION_DEPENDENCY_COUNTS = gql`
  query GetTransactionDependencyCounts($transactionId: ID!) {
    getTransactionDependencyCounts(transactionId: $transactionId) {
      documents
      feeTransactions
      deleteBlockedReason
    }
  }
`;

const PRODUCT_TRANSACTION_TYPES = gql`
  query ProductTransactionTypes($productId: ID!, $includeDeleted: Boolean) {
    productTransactionTypes(productId: $productId, includeDeleted: $includeDeleted)
  }
`;

const PRODUCT_TRANSACTION_TYPE_COUNTS = gql`
  query ProductTransactionTypeCounts($productId: ID!, $includeDeleted: Boolean) {
    productTransactionTypeCounts(productId: $productId, includeDeleted: $includeDeleted) {
      type
      count
    }
  }
`;

// Lightweight query for linking transactions to tasks
const FIND_TRANSACTIONS_FOR_LINKING = gql`
  query FindTransactionsForLinking(
    $searchTerm: String
    $transactionTypes: [String]
    $transactionStatuses: [String]
    $minAmount: Float
    $maxAmount: Float
    $limit: Int
  ) {
    findTransactionsForLinking(
      searchTerm: $searchTerm
      transactionTypes: $transactionTypes
      transactionStatuses: $transactionStatuses
      minAmount: $minAmount
      maxAmount: $maxAmount
      limit: $limit
    ) {
      _id
      friendlyId
      transactionType
      transactionStatus
      transactionAmount
      owner
      denormalized {
        donorName
        senderName
        recipientName
      }
    }
  }
`;

export {
  DONOR_GRANT_TRANSACTIONS,
  PRODUCT_TRANSACTIONS,
  FIND_PRODUCT_TRANSACTIONS_SERVER_SIDE,
  RECENT_TRANSACTIONS,
  FIND_DASHBOARD_PENDING_GRANTS,
  FIND_DASHBOARD_COMPLIANCE_REVIEW_GRANTS,
  FIND_DASHBOARD_IN_TRANSIT_GRANTS,
  FIND_DASHBOARD_FLOATING_GRANTS,
  FIND_ALL_INCOMING_TRANSACTIONS,
  FIND_ALL_OUTGOING_TRANSACTIONS,
  PRODUCT_TRANSACTION_TYPES,
  PRODUCT_TRANSACTION_TYPE_COUNTS,
  FIND_TRANSACTIONS_FOR_LINKING,
  GET_TRANSACTION_DEPENDENCY_COUNTS,
};
